import type { ReactNode } from "react";

export type SegmentedOption<T extends string | number> = {
  value: T;
  label: ReactNode;
  title?: string;
  ariaLabel?: string;
};

type Props<T extends string | number> = {
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  label?: string;
  role?: "group" | "tablist";
  optionRole?: "button" | "tab";
};

export default function SegmentedControl<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  label,
  role = "group",
  optionRole = "button",
}: Props<T>) {
  return (
    <div
      role={role}
      aria-label={ariaLabel}
      className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5 text-sm shadow-sm"
    >
      {label ? (
        <span className="px-2 text-xs font-medium uppercase tracking-wide text-slate-400">
          {label}
        </span>
      ) : null}
      {options.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role={optionRole === "tab" ? "tab" : undefined}
            aria-selected={optionRole === "tab" ? active : undefined}
            aria-pressed={optionRole === "button" ? active : undefined}
            aria-label={option.ariaLabel}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={
              active
                ? "rounded bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800 ring-1 ring-emerald-100"
                : "rounded px-2.5 py-1 font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900"
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
